import { useMutation, useQuery } from "@apollo/client"
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { profileSelector, updateUser } from "reducers/profileSlice"

import { EMPLOYEE_ACCESS, EMPLOYEE_DEPARTMENTS, EMPLOYEES, UPDATE_EMPLOYEE } from "./graphql"

type Employee = {
  attendee?:
    | ({
        isResigned?: boolean | null
        keycloakUsername?: null | string
        telegramUsername?: null | string
      } & NonNullable<User["attendee"]>)
    | null
} & User
const positions: Record<string, string> = {
  ACCOUNTANT: "Kế toán",
  BA: "Business Analyst",
  BE: "Backend Engineer",
  BO: "Back Office",
  CO_FOUNDER: "Co-Founder",
  DESIGN: "Designer",
  DEVOPS: "DevOps",
  DIRECTOR: "Giám đốc",
  FE: "Frontend Engineer",
  FULL_STACK: "Fullstack Engineer",
  HR_ADMIN_MANAGER: "Trưởng phòng Nhân sự tổng hợp",
  HR_SPECIALIST: "Chuyên viên Nhân sự",
  INTERN: "Thực tập sinh",
  MOBILE: "Mobile Engineer",
  OTHER: "Khác",
  PM: "Project Manager",
  QA: "Quality Assurance",
  SA: "Solution Architect",
}
const labels: Record<string, string> = {
  department: "Phòng ban / bộ phận",
  dob: "Ngày sinh",
  email: "Email công ty",
  employeeCode: "Mã nhân viên",
  fullName: "Họ và tên",
  gender: "Giới tính",
  joinedCompanyAt: "Ngày vào công ty",
  name: "Tên hiển thị",
  personalEmail: "Email cá nhân",
  phoneNumber: "Số điện thoại",
  position: "Vị trí",
}
const genders: Record<string, string> = {
  FEMALE: "Nữ",
  MALE: "Nam",
  OTHER: "Khác",
  UNSPECIFIED: "Chưa xác định",
}
const dateOnly = (value?: null | string) => value?.slice(0, 10) || ""
const displayDate = (value?: null | string) => dateOnly(value).split("-").reverse().join("/") || "—"
const incomplete = (employee: Employee) =>
  [
    employee.fullName,
    employee.name,
    employee.gender,
    employee.dob,
    employee.personalEmail,
    employee.phoneNumber,
    employee.attendee?.keycloakUsername,
    employee.employeeCode,
    employee.email,
    employee.department,
    employee.position,
    employee.joinedCompanyAt,
  ].some((value) => !value?.trim())
const toForm = (employee: Employee): Record<string, boolean | string> =>
  Object.fromEntries([
    ...Object.keys(labels).map((key) => [
      key,
      key === "dob" || key === "joinedCompanyAt"
        ? dateOnly(employee[key as keyof Employee] as string)
        : employee[key as keyof Employee] || "",
    ]),
    ["keycloakUsername", employee.attendee?.keycloakUsername || ""],
    ["telegramUsername", employee.attendee?.telegramUsername || ""],
    ["isPartTime", !!employee.attendee?.isPartTime],
    ["isResigned", !!employee.attendee?.isResigned],
  ])

const EmployeeEditor = ({
  canEdit,
  employee,
  onClose,
  onSaved,
}: {
  canEdit: boolean
  employee: Employee
  onClose: () => void
  onSaved: () => Promise<unknown>
}) => {
  const dispatch = useDispatch()
  const { user } = useSelector(profileSelector)
  const [form, setForm] = useState(() => toForm(employee))
  const [error, setError] = useState("")
  const [confirm, setConfirm] = useState(false)
  const [save, { loading }] = useMutation(UPDATE_EMPLOYEE)
  const departments = useQuery<{ employeeDepartments: string[] }>(EMPLOYEE_DEPARTMENTS, { fetchPolicy: "network-only" })
  const departmentOptions = Array.from(
    new Set([...(departments.data?.employeeDepartments || []), ...(employee.department ? [employee.department] : [])]),
  )
  const baseline = toForm(employee)
  const changes = Object.fromEntries(Object.entries(form).filter(([key, value]) => value !== baseline[key]))
  const submit = async () => {
    if (!canEdit) return
    setConfirm(false)
    setError("")
    if (Object.values(changes).some((value) => typeof value === "string" && !value.trim())) {
      setError("Không được xóa trống thông tin đã có. Hãy nhập giá trị thay thế.")
      return
    }
    try {
      const result = await save({
        variables: { id: employee.id, input: changes },
      })
      if (user?.id === employee.id) dispatch(updateUser({ ...user, ...result.data.updateEmployee }))
      enqueueSnackbar("Đã cập nhật hồ sơ nhân viên", { variant: "success" })
      onClose()
      await onSaved()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Không thể lưu thông tin")
    }
  }
  const renderField = (key: string) => {
    const label = labels[key]
    const options = key === "gender" ? genders : undefined
    const isDate = key === "dob" || key === "joinedCompanyAt"
    if (key === "position")
      return (
        <Box key={key} sx={{ minWidth: 0 }}>
          <Autocomplete
            disableClearable={!!form.position}
            disabled={loading || !canEdit}
            getOptionLabel={(value) => positions[value] || value}
            onChange={(_, value) => {
              if (value) setForm((current) => ({ ...current, position: value }))
            }}
            options={Object.keys(positions)}
            renderInput={(params) => <TextField {...params} label={label} placeholder="Tìm vị trí…" />}
            value={String(form.position || "") || null}
          />
        </Box>
      )
    if (key === "department")
      return (
        <Box key={key} sx={{ minWidth: 0 }}>
          <Autocomplete
            disabled={loading || !canEdit}
            freeSolo
            inputValue={String(form.department || "")}
            loading={departments.loading}
            onChange={(_, value) => setForm((current) => ({ ...current, department: value || "" }))}
            onInputChange={(_, value, reason) => {
              if (reason === "input" || reason === "clear") setForm((current) => ({ ...current, department: value }))
            }}
            onOpen={() => {
              if (departments.error) void departments.refetch()
            }}
            options={departmentOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText={departments.error ? "Không tải được gợi ý; có thể nhập trực tiếp" : undefined}
                label={label}
              />
            )}
            value={String(form.department || "")}
          />
        </Box>
      )
    return (
      <Box key={key} sx={{ minWidth: 0 }}>
        <TextField
          disabled={loading || !canEdit}
          fullWidth
          InputLabelProps={isDate ? { shrink: true } : undefined}
          label={label}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              [key]: event.target.value,
            }))
          }
          select={!!options}
          type={isDate ? "date" : key === "email" || key === "personalEmail" ? "email" : "text"}
          value={form[key]}
        >
          {options && [
            <MenuItem disabled key="empty" value="">
              Chưa cập nhật
            </MenuItem>,
            ...Object.entries(options).map(([value, text]) => (
              <MenuItem key={value} value={value}>
                {text}
              </MenuItem>
            )),
          ]}
        </TextField>
      </Box>
    )
  }
  return (
    <Drawer
      anchor="right"
      onClose={() => !loading && onClose()}
      open
      PaperProps={{ sx: { p: 3, width: { sm: 560, xs: "100%" } } }}
    >
      <Stack spacing={3}>
        <Stack alignItems="center" direction="row" spacing={2}>
          <Avatar src={employee.attendee?.discordAvatarUrl || undefined} />
          <Box flex={1}>
            <Typography variant="h6">{employee.fullName || employee.name || employee.username}</Typography>
            <Typography color="text.secondary">{employee.username}</Typography>
          </Box>
          <Button disabled={loading} onClick={onClose}>
            Đóng
          </Button>
        </Stack>
        <Box component="section" sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
          <Typography fontWeight={700} variant="subtitle1">
            Thông tin cá nhân
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { sm: "repeat(2, minmax(0, 1fr))", xs: "minmax(0, 1fr)" },
              mt: 2,
            }}
          >
            {["fullName", "name", "gender", "dob", "personalEmail", "phoneNumber"].map(renderField)}
          </Box>
        </Box>
        <Box component="section" sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
          <Typography fontWeight={700} variant="subtitle1">
            Thông tin công việc
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { sm: "repeat(2, minmax(0, 1fr))", xs: "minmax(0, 1fr)" },
              mt: 2,
            }}
          >
            {["employeeCode", "email", "department", "position", "joinedCompanyAt"].map(renderField)}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isPartTime}
                    disabled={!employee.attendee || loading || !canEdit}
                    onChange={(_, checked) => setForm((current) => ({ ...current, isPartTime: checked }))}
                  />
                }
                label="Làm việc part-time"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isResigned}
                    disabled={!employee.attendee || loading || !canEdit}
                    onChange={(_, checked) => setForm((current) => ({ ...current, isResigned: checked }))}
                  />
                }
                label="Đã nghỉ việc"
              />
              <Typography color="text.secondary" display="block" variant="caption">
                Trạng thái làm việc được dùng cho nghiệp vụ nhân sự và nhắc chấm công; không khóa tài khoản đăng nhập.
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box component="section" sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
          <Typography fontWeight={700} variant="subtitle1">
            Tài khoản liên kết
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box>
              {[
                ["Discord ID", employee.attendee?.discordId],
                ["Discord name", employee.attendee?.discordName],
              ].map(([label, value]) => (
                <Typography key={label} sx={{ overflowWrap: "anywhere" }} variant="body2">
                  {label}: {value || "Chưa liên kết"}
                </Typography>
              ))}
            </Box>
            <Stack spacing={2}>
              {[
                ["keycloakUsername", "Keycloak username"],
                ["telegramUsername", "Telegram username"],
              ].map(([key, label]) => (
                <TextField
                  disabled={!canEdit || loading || !employee.attendee}
                  fullWidth
                  inputProps={{ maxLength: 255 }}
                  key={key}
                  label={label}
                  onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
                  size="small"
                  value={form[key]}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {canEdit && (
          <Button
            disabled={loading || !Object.keys(changes).length}
            onClick={() => (changes.isResigned !== undefined ? setConfirm(true) : submit())}
            variant="contained"
          >
            {loading ? "Đang lưu…" : "Lưu thay đổi"}
          </Button>
        )}
      </Stack>
      <Dialog onClose={() => setConfirm(false)} open={confirm}>
        <DialogTitle>Xác nhận thay đổi trạng thái</DialogTitle>
        <DialogContent>
          Chuyển nhân viên sang trạng thái {form.isResigned ? "đã nghỉ việc" : "đang làm việc"}? Hồ sơ và lịch sử được
          giữ nguyên.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Hủy</Button>
          <Button onClick={submit}>Xác nhận và lưu</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}

const EmployeesBoard = () => {
  const access = useQuery<{ canManageEmployees: boolean; canViewEmployees: boolean }>(EMPLOYEE_ACCESS, {
    fetchPolicy: "network-only",
  })
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [filters, setFilters] = useState({
    department: "",
    incomplete: "",
    position: "",
    status: "active",
    type: "",
  })
  const [pagination, setPagination] = useState({ page: 0, pageSize: 20 })
  const [sort, setSort] = useState<GridSortModel>([{ field: "fullName", sort: "asc" }])
  const [selected, setSelected] = useState<Employee | null>(null)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPagination((current) => ({ ...current, page: 0 }))
    }, 350)
    return () => clearTimeout(timer)
  }, [search])
  const { data, error, loading, previousData, refetch } = useQuery<{
    employeesPage: { items: Employee[]; total: number }
  }>(EMPLOYEES, {
    fetchPolicy: "network-only",
    skip: !access.data?.canViewEmployees,
    variables: {
      department: filters.department || undefined,
      descending: sort[0]?.sort === "desc",
      incomplete: filters.incomplete ? true : undefined,
      isPartTime: filters.type ? filters.type === "part" : undefined,
      isResigned: filters.status ? filters.status === "resigned" : undefined,
      limit: pagination.pageSize,
      offset: pagination.page * pagination.pageSize,
      position: filters.position || undefined,
      search: debouncedSearch || undefined,
      sortBy: sort[0]?.field === "fullName" ? "name" : sort[0]?.field || "name",
    },
  })
  const filter = (key: keyof typeof filters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }))
    setPagination((current) => ({ ...current, page: 0 }))
  }
  const columns: GridColDef<Employee>[] = [
    {
      field: "fullName",
      flex: 1,
      headerName: "Nhân viên",
      minWidth: 230,
      renderCell: ({ row }) => (
        <Stack alignItems="center" direction="row" spacing={1}>
          <Avatar src={row.attendee?.discordAvatarUrl || undefined} sx={{ height: 32, width: 32 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap title={row.fullName || row.name || row.username} variant="body2">
              {row.fullName || row.name || row.username}
            </Typography>
            <Typography color="text.secondary" noWrap title={row.email || row.username} variant="caption">
              {row.email || row.username}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    { field: "employeeCode", headerName: "Mã NV", width: 110 },
    {
      field: "department",
      headerName: "Phòng ban",
      sortable: false,
      width: 140,
    },
    {
      field: "position",
      headerName: "Vị trí",
      sortable: false,
      valueFormatter: ({ value }) => positions[value] || "—",
      width: 170,
    },
    {
      field: "joinedCompanyAt",
      headerName: "Ngày vào",
      valueFormatter: ({ value }) => displayDate(value),
      width: 120,
    },
    {
      field: "type",
      headerName: "Loại làm việc",
      sortable: false,
      valueGetter: ({ row }) => (!row.attendee ? "Chưa liên kết" : row.attendee.isPartTime ? "Part-time" : "Full-time"),
      width: 120,
    },
    {
      field: "status",
      headerName: "Trạng thái",
      renderCell: ({ row }) => (
        <Chip
          color={row.attendee && !row.attendee.isResigned ? "success" : "default"}
          label={!row.attendee ? "Chưa liên kết" : row.attendee.isResigned ? "Đã nghỉ" : "Đang làm"}
          size="small"
        />
      ),
      sortable: false,
      width: 140,
    },
    {
      field: "incomplete",
      headerName: "Hồ sơ",
      renderCell: ({ row }) =>
        incomplete(row) ? (
          <Chip color="warning" label="Chưa đủ thông tin" size="small" />
        ) : (
          <Typography variant="body2">Đã đầy đủ</Typography>
        ),
      sortable: false,
      width: 190,
    },
    {
      align: "center",
      disableColumnMenu: true,
      field: "actions",
      headerName: "",
      renderCell: ({ row }) => (
        <Button
          onClick={() => setSelected(row)}
          size="small"
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          variant="text"
        >
          {access.data?.canManageEmployees ? "Xem / sửa" : "Xem"}
        </Button>
      ),
      sortable: false,
      width: 132,
    },
  ]
  if (access.loading) return <CircularProgress />
  if (access.error)
    return (
      <Alert action={<Button onClick={() => access.refetch()}>Thử lại</Button>} severity="error">
        {access.error.message}
      </Alert>
    )
  if (!access.data?.canViewEmployees) return <Alert severity="warning">Bạn không có quyền xem nhân viên.</Alert>
  return (
    <Stack
      spacing={2}
      sx={{
        "& > :not(.employee-table)": { flexShrink: 0 },
        flex: 1,
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box>
        <Typography fontWeight={700} variant="h5">
          Nhân viên
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Cập nhật hồ sơ sau khi nhân viên đăng nhập Discord.
        </Typography>
      </Box>
      <Box
        sx={{
          "& > .MuiFormControl-root": { minWidth: 0, width: "100%" },
          alignItems: "start",
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            lg: "repeat(4, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "minmax(280px, 2fr) repeat(5, minmax(130px, 1fr)) auto",
            xs: "minmax(0, 1fr)",
          },
        }}
      >
        <TextField
          label="Tìm nhân viên"
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Tên, mã NV, email công ty/cá nhân, Discord, Keycloak"
          size="small"
          value={search}
        />
        <TextField
          label="Phòng ban"
          onChange={(event) => filter("department", event.target.value)}
          size="small"
          value={filters.department}
        />
        <TextField
          InputLabelProps={{ shrink: true }}
          label="Vị trí"
          onChange={(event) => filter("position", event.target.value)}
          select
          SelectProps={{ displayEmpty: true }}
          size="small"
          value={filters.position}
        >
          <MenuItem value="">Tất cả</MenuItem>
          {Object.entries(positions).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          InputLabelProps={{ shrink: true }}
          label="Loại làm việc"
          onChange={(event) => filter("type", event.target.value)}
          select
          SelectProps={{ displayEmpty: true }}
          size="small"
          value={filters.type}
        >
          <MenuItem value="">Tất cả</MenuItem>
          <MenuItem value="full">Full-time</MenuItem>
          <MenuItem value="part">Part-time</MenuItem>
        </TextField>
        <TextField
          InputLabelProps={{ shrink: true }}
          label="Trạng thái"
          onChange={(event) => filter("status", event.target.value)}
          select
          SelectProps={{ displayEmpty: true }}
          size="small"
          value={filters.status}
        >
          <MenuItem value="">Tất cả</MenuItem>
          <MenuItem value="active">Đang làm</MenuItem>
          <MenuItem value="resigned">Đã nghỉ</MenuItem>
        </TextField>
        <TextField
          InputLabelProps={{ shrink: true }}
          label="Hồ sơ"
          onChange={(event) => filter("incomplete", event.target.value)}
          select
          SelectProps={{ displayEmpty: true }}
          size="small"
          value={filters.incomplete}
        >
          <MenuItem value="">Tất cả</MenuItem>
          <MenuItem value="yes">Chưa đủ thông tin</MenuItem>
        </TextField>
        <Button onClick={() => refetch()} sx={{ height: 40, px: 2, whiteSpace: "nowrap" }}>
          Làm mới
        </Button>
      </Box>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Box className="employee-table" sx={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%" }}>
        <DataGrid
          columns={columns}
          disableColumnFilter
          disableRowSelectionOnClick
          getRowHeight={() => 72}
          loading={loading}
          localeText={{ noRowsLabel: "Không có nhân viên phù hợp" }}
          onPaginationModelChange={setPagination}
          onSortModelChange={(value) => {
            setSort(value)
            setPagination((current) => ({ ...current, page: 0 }))
          }}
          pageSizeOptions={[20, 50, 100]}
          paginationMode="server"
          paginationModel={pagination}
          rowCount={data?.employeesPage.total ?? previousData?.employeesPage.total ?? 0}
          rowHeight={72}
          rows={data?.employeesPage.items || []}
          sortingMode="server"
          sortModel={sort}
          sx={{
            "& .MuiDataGrid-cell": { alignItems: "center" },
            "& .MuiDataGrid-cellContent": { overflow: "hidden", textOverflow: "ellipsis" },
          }}
        />
      </Box>
      {selected && (
        <EmployeeEditor
          canEdit={!!access.data?.canManageEmployees}
          employee={selected}
          key={selected.id}
          onClose={() => setSelected(null)}
          onSaved={refetch}
        />
      )}
    </Stack>
  )
}
export default EmployeesBoard
